import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useCart } from '@/contexts/cart-context';
import { quickSaleService } from '@/services/quick-sale.service';
import { useToast } from '@caruso/ui';

export function QuickSalePage() {
  const { clientId, productId } = useParams<{ clientId: string; productId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addItem, clearCart } = useCart();

  const { data, isLoading, error } = useQuery({
    queryKey: ['quick-sale', clientId, productId],
    queryFn: () => quickSaleService.getQuickSaleData(clientId!, productId!),
    enabled: !!clientId && !!productId,
    retry: false,
  });

  useEffect(() => {
    if (data) {
      // Limpar carrinho para garantir que só tem o produto da venda rápida
      clearCart();

      // Adicionar produto ao carrinho
      addItem({
        id: data.product.id,
        name: data.product.name,
        price: data.product.finalPrice,
        image: data.product.image || undefined,
        stock: data.product.stock || undefined,
      }, 1);

      // Salvar dados do cliente no localStorage para pré-preencher o checkout
      const clientData = {
        customerName: data.client.name,
        customerEmail: data.client.email || '',
        customerPhone: data.client.phone || '',
        zipCode: data.client.address.zipCode || '',
        address: data.client.address.address || '',
        number: data.client.address.number || '',
        complement: data.client.address.complement || '',
        neighborhood: data.client.address.neighborhood || '',
        city: data.client.address.city || '',
        state: data.client.address.state || '',
      };

      localStorage.setItem('@caruso:quick-sale-client', JSON.stringify(clientData));

      // Mostrar mensagem de sucesso
      toast({
        title: 'Produto adicionado!',
        description: data.product.discount > 0
          ? `${data.product.name} com ${data.product.discount}% de desconto aplicado!`
          : `${data.product.name} adicionado ao carrinho`,
        variant: 'success',
      });

      // Redirecionar para checkout após um pequeno delay
      setTimeout(() => {
        navigate('/loja/checkout');
      }, 1000);
    }
  }, [data, addItem, clearCart, navigate, toast]);

  useEffect(() => {
    if (error) {
      toast({
        title: 'Erro ao processar venda',
        description: error instanceof Error ? error.message : 'Link inválido ou expirado',
        variant: 'destructive',
      });

      // Redirecionar para a loja após erro
      setTimeout(() => {
        navigate('/loja');
      }, 2000);
    }
  }, [error, navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brand-smoke via-brand-ink to-brand-smoke">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-brand-gold mx-auto mb-4" />
          <h2 className="text-2xl font-serif font-bold text-brand-gold mb-2">Processando sua venda...</h2>
          <p className="text-brand-ivory/60">Por favor, aguarde</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brand-smoke via-brand-ink to-brand-smoke">
        <div className="text-center">
          <div className="h-16 w-16 rounded-full bg-red-500/20 border-2 border-red-500/40 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-serif font-bold text-brand-gold mb-2">Ops! Algo deu errado</h2>
          <p className="text-brand-ivory/70 mb-4">
            {error instanceof Error ? error.message : 'Link inválido ou expirado'}
          </p>
          <p className="text-sm text-brand-ivory/50">Redirecionando para a loja...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brand-smoke via-brand-ink to-brand-smoke">
      <div className="text-center">
        <div className="h-16 w-16 rounded-full bg-brand-gold/20 border-2 border-brand-gold flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl text-brand-gold">✓</span>
        </div>
        <h2 className="text-2xl font-serif font-bold text-brand-gold mb-2">Produto adicionado!</h2>
        <p className="text-brand-ivory/60">Redirecionando para o checkout...</p>
      </div>
    </div>
  );
}
